import { useEffect, useState, type RefObject } from "react";

interface Props {
  /** For webm: the <video> element we observe via timeupdate. */
  videoRef?: RefObject<HTMLVideoElement>;
  /** For gif/apng: the loop duration in seconds (from e621 metadata). */
  duration?: number | null;
  /** When false, the timeline hides and any timer is torn down. */
  active: boolean;
  className?: string;
}

/**
 * Slim playback indicator overlaid on animated media.
 *
 * - webm  → exact, driven by the <video> element's timeupdate event.
 * - gif / apng → approximate, driven by a JS timer seeded with post.duration.
 *   Browsers don't expose any decode/playback API for animated images, so the
 *   indicator may drift slightly from what's actually on screen. For thumbnails
 *   this is fine; if you want sample-accurate progress, the GIF would have to
 *   be decoded in JS (~80KB lib) or transcoded to webm server-side.
 */
export function PlaybackTimeline({ videoRef, duration, active, className }: Props) {
  const [pct, setPct] = useState(0);

  // Video mode: read currentTime / duration on every timeupdate.
  useEffect(() => {
    const v = videoRef?.current;
    if (!v || !active) return;
    const onUpdate = () => {
      if (v.duration && Number.isFinite(v.duration) && v.duration > 0) {
        setPct((v.currentTime / v.duration) * 100);
      }
    };
    v.addEventListener("timeupdate", onUpdate);
    v.addEventListener("seeked", onUpdate);
    return () => {
      v.removeEventListener("timeupdate", onUpdate);
      v.removeEventListener("seeked", onUpdate);
    };
  }, [videoRef, active]);

  // Timer mode (gif / apng): assume playback starts when this component mounts
  // and loops every `duration` seconds. Refresh ~10 times/sec.
  useEffect(() => {
    if (videoRef) return; // video path owns this
    if (!active || !duration || duration <= 0) return;
    const start = performance.now();
    const id = window.setInterval(() => {
      const elapsed = (performance.now() - start) / 1000;
      setPct(((elapsed % duration) / duration) * 100);
    }, 100);
    return () => window.clearInterval(id);
  }, [videoRef, duration, active]);

  if (!active) return null;
  if (!videoRef && !duration) return null; // nothing usable to show

  return (
    <div
      className={`absolute bottom-0 inset-x-0 h-1 bg-black/40 ${className ?? ""}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
    >
      <div
        className="h-full bg-accent"
        style={{ width: `${pct}%`, transition: "width 100ms linear" }}
      />
    </div>
  );
}
